<template>
	<j-card class="mb2 bg5">
		<j-accordion colorWhenClosed="bg0" :startOpen="true">
			<div slot="title" class="fs4 fw7">Policy Info Facts</div>
			<div class="p2" slot="body">
				<j-list :list="schema" class="b1 r2 bc7 bg5">
					<div class="flex xs-12 p3" slot="list" slot-scope="{ item }">
						<div class="flex xs-6 xs-left xs-middle fw7 c6 lh125">{{ item.label }}</div>
						<div class="flex xs-4 xs-left xs-middle lh125">{{ form[item.type] }}</div>
						<div class="flex xs-2 xs-right">
							<crm-icon
								name="edit"
								size="small"
								color="c2"
								@click.native="openModal(item)"
							/>
						</div>
					</div>
				</j-list>
			</div>
		</j-accordion>

		<!-- Modal with update/create form -->
		<core-modal name="aegPolicyFacts" headline="Update" @close="$emit('close')">
			<div class="p4">
				<!-- Show only field that is updating -->
				<core-form-input
					v-for="field in computedSchema"
					:key="field.type"
					:name="field.type"
					:componentName="field.component"
					:label="field.label"
					v-model="form[field.type]"
					:validationErrors="errors"
					v-bind="field.props"
				/>

				<!-- Button -->
				<div class="xs-12 mt6">
					<j-button @click="onSave" :isLoading="isLoading">Update</j-button>
				</div>
			</div>
		</core-modal>
	</j-card>
</template>

<script>
import JCard from "@/components/JCard"
import JButton from "@/components/JButton"
import JAccordion from "@/components/JAccordion"
import JList from "@/components/JList"
import CrmIcon from "@/components/CrmIcon"
import CoreModal from "@/modules/core/components/CoreModal/"
import CoreFormInput from "@/modules/core/components/CoreFormInput/"
import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"

export default {
	components: {
		JCard,
		JButton,
		JAccordion,
		JList,
		CrmIcon,
		CoreModal,
		CoreFormInput
	},

	props: {
		// List of facts
		facts: {
			type: Array,
			default: () => []
		},

		policyId: {
			type: String
		}
	},

	data() {
		return {
			isLoading: false,
			form: {
				certificateFormNumber: null,
				policyDeliveryMethod: null,
				policyDeliveryDate: null,
				proposalAcceptanceDate: null,
				issuerNumberOtherInsurers: null,
				policyOrGroupReference: null
			},

			// NOTE: when adding new field, make sure that `type` is defined in this.form
			schema: [
				{
					label: "Attached to Certificate Form Number",
					component: "j-base-select",
					type: "certificateFormNumber",
					isUpdating: false,
					props: {
						options: [
							{
								name: "Exhibition/04",
								value: "Exhibition/04"
							},
							{
								name: "Jeweller's Block/01",
								value: "Jeweller's Block/01"
							},
							{
								name: "Personal Jewellery/06",
								value: "Personal Jewellery/06"
							},
							{
								name: "Tour/03",
								value: "Tour/03"
							}
						]
					}
				},
				{
					label: "Policy Delivery",
					component: "j-base-select",
					type: "policyDeliveryMethod",
					isUpdating: false,
					props: {
						options: [
							{
								name: "Hand Delivery",
								value: "Hand Delivery"
							},
							{
								name: "Not Delivered",
								value: "Not Delivered"
							},
							{
								name: "Post Delivery",
								value: "Post Delivery"
							}
						]
					}
				},
				{
					label: "Policy Delivery Date",
					component: "j-base-date-picker-simple",
					dataType: "date",
					type: "policyDeliveryDate",
					isUpdating: false
				},
				{
					label: "Proposal Accepted Date",
					component: "j-base-date-picker-simple",
					dataType: "date",
					type: "proposalAcceptanceDate",
					isUpdating: false
				},
				{
					label: "Policy Number (other insurers)",
					component: "j-base-input-text",
					type: "issuerNumberOtherInsurers",
					isUpdating: false
				}
			]
		}
	},

	computed: {
		computedSchema() {
			return this.schema.filter(field => field.isUpdating)
		}
	},

	methods: {
		openModal(fieldToUpdate) {
			// Set field we are updating in schema
			this.schema.forEach(field => {
				field.isUpdating = false // reset
				if (fieldToUpdate.type === field.type) field.isUpdating = true // set
			})
			this.$modal.show("aegPolicyFacts")
		},

		async onSave() {
			// Find field that is supposed to be processed
			const fieldToProcess = this.computedSchema?.find(
				field => field.isUpdating
			)

			if (!fieldToProcess) return

			this.isLoading = true

			// Update
			if (fieldToProcess.action === "update") {
				const res = await this.updateFact({
					policyId: this.policyId,
					factId: this.facts?.find(fact => fact.type === fieldToProcess.type)
						?.id,
					input: {
						type: fieldToProcess.type,
						value: {
							[`${fieldToProcess.dataType || "string"}Value`]: this.form[
								fieldToProcess.type
							]
						}
					}
				})

				// Check errors
				if (res?.data?.updatePolicyFact?.status !== "success") {
					this.$store.dispatch(
						"showError",
						res?.data?.updatePolicyFact?.errors?.[0] ||
							res?.errors?.[0] ||
							this.$t("errors.general")
					)
					this.isLoading = false
					this.form[fieldToProcess.type] = "" // Reset form to signify error
					return
				}
			}

			// Update
			if (fieldToProcess.action === "add") {
				const res = await this.addFact({
					policyId: this.policyId,
					input: {
						type: fieldToProcess.type,
						value: {
							[`${fieldToProcess.dataType || "string"}Value`]: this.form[
								fieldToProcess.type
							]
						}
					}
				})

				// Check errors
				if (res?.data?.addPolicyFact?.status !== "success") {
					this.$store.dispatch(
						"showError",
						res?.data?.addPolicyFact?.errors?.[0] ||
							res?.errors?.[0] ||
							this.$t("errors.general")
					)
					this.isLoading = false
					this.form[fieldToProcess.type] = "" // Reset form to signify error
					return
				}
			}

			this.isLoading = false
			this.$modal.hide("aegPolicyFacts")
		},

		async addFact(variables) {
			const query = gql`
				mutation addPolicyFact($policyId: String!, $input: addFactInput!) {
					addPolicyFact(policyId: $policyId, input: $input) {
						status
						errors
					}
				}
			`
			const res = await api({ query, variables })
			return Promise.resolve(res)
		},

		async updateFact(variables) {
			const query = gql`
				mutation updatePolicyFact(
					$policyId: String!
					$factId: String!
					$input: updateFactInput!
				) {
					updatePolicyFact(
						policyId: $policyId
						factId: $factId
						input: $input
					) {
						status
						errors
					}
				}
			`
			const res = await api({ query, variables })
			return Promise.resolve(res)
		}
	},

	watch: {
		facts: {
			handler() {
				// Check what facts should be added and what facts should be updated
				this.schema.forEach(field => {
					// If it is in this.facts, we set update flag
					if (this.facts.find(fact => fact.type === field.type))
						field.action = "update"
					else field.action = "add"
				})

				// Populate this.form with data
				this.schema.forEach(field => {
					this.form[field.type] = this.facts.find(
						fact => fact.type === field.type
					)?.value?.[`${field.dataType || "string"}Value`]
				})
			},
			immediate: true
		}
	}
}
</script>
